// ペンストロークの当たり判定に使う定数と純粋な幾何ヘルパー。
//
// クリック選択の当たり判定はブラウザの SVG ヒットテスト（pointer-events）へ委ねるため、
// ここで扱うのは DOM を使えない範囲指定（マーキー）選択の判定だけである。
// ストロークを「points を結んだ折れ線に半径 thickness / 2 + margin の丸みを与えた帯」と
// 見なし、マーキー矩形との距離で交差を判定する。DOM とストアには依存しない。
//
// 描画（perfect-freehand の実アウトライン）と判定（折れ線の近似帯）で幾何が 2 系統になるが、
// 近似帯の半径は実アウトラインの半幅（thinning: 0.5 のため最大でも thickness / 2）へ
// マージンを足したものなので、乖離は「近似帯のほうがわずかに広い」方向に生じる。
// 厳密な包含は保証しないため、呼び出し側（marquee.ts）は「枠が外接矩形を完全に包含する
// 場合は必ず選択」という条件を論理和で併用し、最悪ケースを排除する。

/** ペンストロークの当たり判定・選択輪郭に与える画面上のマージン（px）。 */
export const PEN_HIT_MARGIN_SCREEN_PX = 6;

/** 選択輪郭線の画面上の太さ（px）。他オブジェクトの選択枠（2px）と揃える。 */
export const PEN_SELECTION_RING_SCREEN_PX = 2;

export interface PenHitPoint {
  readonly x: number;
  readonly y: number;
}

/** 判定対象の矩形（ステージ座標）。w / h は非負であることを前提とする。 */
export interface PenHitRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * 折れ線（points）を太さ thickness の帯として扱い、margin だけ広げた領域が
 * rect と交差するかを返す。境界の接触も交差扱いとする。
 * pressure は判定に使わない（x / y だけを見る）。
 */
export function penStrokeIntersectsRect(
  points: readonly PenHitPoint[],
  thickness: number,
  margin: number,
  rect: PenHitRect
): boolean {
  if (points.length === 0) return false;
  const radius = toNonNegativeFinite(thickness) / 2 + toNonNegativeFinite(margin);
  const first = points[0];
  if (!first) return false;
  if (points.length === 1) {
    return pointToRectDistance(first, rect) <= radius;
  }
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    // noUncheckedIndexedAccess のため添字アクセスの結果を undefined チェックしてから使う。
    if (!a || !b) continue;
    if (segmentToRectDistance(a, b, rect) <= radius) return true;
  }
  return false;
}

/** 負値・NaN・Infinity を 0 へ丸める（半径計算へ異常値を持ち込まないため）。 */
function toNonNegativeFinite(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** 点と矩形の距離。点が矩形の内側（境界含む）なら 0。 */
function pointToRectDistance(p: PenHitPoint, rect: PenHitRect): number {
  const dx = Math.max(rect.x - p.x, 0, p.x - (rect.x + rect.w));
  const dy = Math.max(rect.y - p.y, 0, p.y - (rect.y + rect.h));
  return Math.hypot(dx, dy);
}

/** 点と線分 ab の距離。a と b が同一点の場合は点間距離。 */
function pointToSegmentDistance(p: PenHitPoint, a: PenHitPoint, b: PenHitPoint): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSq = abx * abx + aby * aby;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const raw = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSq;
  const t = Math.min(1, Math.max(0, raw));
  return Math.hypot(p.x - (a.x + t * abx), p.y - (a.y + t * aby));
}

/**
 * 線分 ab と矩形が交差するか（接触も交差扱い）。
 * 分離軸定理を x 軸・y 軸・線分の法線の 3 軸で判定する。線分は退化した OBB なので、
 * この 3 軸で分離できなければ交差している。
 */
function segmentIntersectsRect(a: PenHitPoint, b: PenHitPoint, rect: PenHitRect): boolean {
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;
  // 軸 1・2: 線分の外接矩形と rect が離れていれば交差しない。
  if (Math.max(a.x, b.x) < rect.x || Math.min(a.x, b.x) > right) return false;
  if (Math.max(a.y, b.y) < rect.y || Math.min(a.y, b.y) > bottom) return false;
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  // 退化した線分（点）は法線を持たない。上の 2 軸で分離できていない＝矩形の内側にある。
  if (abx === 0 && aby === 0) return true;
  // 軸 3: rect の 4 頂点がすべて線分の直線の同じ側にあれば交差しない。
  const corners: readonly PenHitPoint[] = [
    { x: rect.x, y: rect.y },
    { x: right, y: rect.y },
    { x: right, y: bottom },
    { x: rect.x, y: bottom },
  ];
  let hasPositive = false;
  let hasNegative = false;
  for (const corner of corners) {
    const cross = abx * (corner.y - a.y) - aby * (corner.x - a.x);
    // 頂点が直線上にある場合は、この軸では分離できない（接触扱い）。
    if (cross === 0) return true;
    if (cross > 0) hasPositive = true;
    else hasNegative = true;
  }
  return hasPositive && hasNegative;
}

/** 線分 ab と矩形の距離。交差していれば 0。 */
function segmentToRectDistance(a: PenHitPoint, b: PenHitPoint, rect: PenHitRect): number {
  if (segmentIntersectsRect(a, b, rect)) return 0;
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;
  return Math.min(
    pointToRectDistance(a, rect),
    pointToRectDistance(b, rect),
    pointToSegmentDistance({ x: rect.x, y: rect.y }, a, b),
    pointToSegmentDistance({ x: right, y: rect.y }, a, b),
    pointToSegmentDistance({ x: right, y: bottom }, a, b),
    pointToSegmentDistance({ x: rect.x, y: bottom }, a, b)
  );
}
